package org.moxultimate.topwithmox.wallpapercrud.main

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.framework.annotations.DelicateApi
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.window
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import org.moxultimate.topwithmox.components.buttons.GradientButton
import org.moxultimate.topwithmox.components.buttons.PressableButtonStyle
import org.moxultimate.topwithmox.components.chip.Chip
import org.moxultimate.topwithmox.components.chip.ChipRowScrollStyle
import org.moxultimate.topwithmox.components.error.ErrorPage
import org.moxultimate.topwithmox.components.image.WallpaperDisplay
import org.moxultimate.topwithmox.components.loading.LoadingIndicator
import org.moxultimate.topwithmox.components.loading.LoadingMoreIndicator
import org.moxultimate.topwithmox.components.loading.OverlayLoadingIndicator
import org.moxultimate.topwithmox.components.searchbar.SearchBarWithAutocomplete
import org.moxultimate.topwithmox.components.texts.moxTextGradient
import org.moxultimate.topwithmox.di.koinViewModel
import org.moxultimate.topwithmox.navigation.rememberAppNavigator
import org.moxultimate.topwithmox.theme.FontFamily
import org.moxultimate.topwithmox.theme.MaxChipFontSize
import org.moxultimate.topwithmox.theme.MinChipFontSize
import org.moxultimate.topwithmox.theme.WhiteBG
import org.moxultimate.topwithmox.wallpaper.data.WallpaperResponse
import org.w3c.dom.events.Event

private val ALL_TYPES = listOf("Phone", "Desktop", "Widescreen", "Vertical")

/** Main CRUD landing page at /wallpaper-crud. Lists wallpapers with search/filter and add/edit controls. */
@Composable
fun WallpaperCRUDPage() {
    val viewModel: WallpaperCRUDViewModel = koinViewModel()
    val listState by viewModel.listState.collectAsState()
    val isLoadingMore by viewModel.isLoadingMore.collectAsState()
    val allSeries by viewModel.allSeries.collectAsState()
    val allTags by viewModel.allTags.collectAsState()
    val searchText by viewModel.searchText.collectAsState()
    val hasMoreInCurrentView by viewModel.hasMoreInCurrentView.collectAsState()
    val selectedTypes by viewModel.selectedTypes.collectAsState()
    val selectedSeries by viewModel.selectedSeries.collectAsState()
    val navigator = rememberAppNavigator()

    // Retain the last successful result so the grid stays visible while
    // a filter chip reload is in flight — prevents the page flickering to blank.
    var lastSuccess by remember { mutableStateOf<WallpaperListState.Success?>(null) }
    if (listState is WallpaperListState.Success) {
        lastSuccess = listState as WallpaperListState.Success
    }

    val isFilterReloading = listState is WallpaperListState.Loading && lastSuccess != null

    LaunchedEffect(Unit) {
        viewModel.loadWallpapers()
        viewModel.loadAllSeries()
        viewModel.loadAllTags()
    }

    Box(
        modifier = Modifier.fillMaxWidth().fillMaxHeight().backgroundColor(WhiteBG),
        contentAlignment = Alignment.Center,
    ) {
        when (val state = listState) {
            is WallpaperListState.Loading -> {
                val cached = lastSuccess
                if (cached != null) {
                    CRUDListContent(
                        state = cached,
                        selectedTypes = selectedTypes,
                        selectedSeries = selectedSeries,
                        allSeries = allSeries,
                        allTags = allTags,
                        searchText = searchText,
                        isLoadingMore = isLoadingMore,
                        hasMoreInCurrentView = hasMoreInCurrentView,
                        onTypeToggle = { viewModel.toggleType(it) },
                        onSeriesToggle = { viewModel.toggleSeries(it) },
                        onSearchChange = { viewModel.setSearchText(it) },
                        onClearFilters = { viewModel.clearAllFilters() },
                        onLoadMore = { viewModel.loadMoreIfNeeded() },
                        onAddClick = { navigator.goToWallpaperAddPage() },
                        onWallpaperClick = { navigator.goToWallpaperUpdatePage(it) },
                    )
                } else {
                    LoadingIndicator()
                }
            }

            is WallpaperListState.Error -> ErrorPage(
                errorCode = state.statusCode.toString(),
                error = state.message,
                onTryAgain = { viewModel.loadWallpapers() },
            )

            is WallpaperListState.Success -> {
                CRUDListContent(
                    state = state,
                    selectedTypes = selectedTypes,
                    selectedSeries = selectedSeries,
                    allSeries = allSeries,
                    allTags = allTags,
                    searchText = searchText,
                    isLoadingMore = isLoadingMore,
                    hasMoreInCurrentView = hasMoreInCurrentView,
                    onTypeToggle = { viewModel.toggleType(it) },
                    onSeriesToggle = { viewModel.toggleSeries(it) },
                    onSearchChange = { viewModel.setSearchText(it) },
                    onClearFilters = { viewModel.clearAllFilters() },
                    onLoadMore = { viewModel.loadMoreIfNeeded() },
                    onAddClick = { navigator.goToWallpaperAddPage() },
                    onWallpaperClick = { navigator.goToWallpaperUpdatePage(it) },
                )
            }
        }

        // Overlay is always in the DOM — CSS opacity transition drives the smooth
        // fade-in and fade-out without flickering the grid content.
        OverlayLoadingIndicator(visible = isFilterReloading)
    }
}

@OptIn(DelicateApi::class)
@Composable
private fun CRUDListContent(
    state: WallpaperListState.Success,
    selectedTypes: Set<String>,
    selectedSeries: Set<String>,
    allSeries: List<String>,
    allTags: List<String>,
    searchText: String,
    isLoadingMore: Boolean,
    hasMoreInCurrentView: Boolean,
    onTypeToggle: (String) -> Unit,
    onSeriesToggle: (String) -> Unit,
    onSearchChange: (String) -> Unit,
    onClearFilters: () -> Unit,
    onLoadMore: () -> Unit,
    onAddClick: () -> Unit,
    onWallpaperClick: (WallpaperResponse) -> Unit,
) {
    DisposableEffect(hasMoreInCurrentView) {
        val handler: (Event) -> Unit = {
            val scrolled = window.scrollY + window.innerHeight
            val total = window.document.documentElement?.scrollHeight ?: 0
            if (hasMoreInCurrentView && scrolled >= total - 300) onLoadMore()
        }
        window.addEventListener("scroll", handler)
        onDispose { window.removeEventListener("scroll", handler) }
    }

    val hasActiveFilters = selectedTypes.isNotEmpty() || selectedSeries.isNotEmpty() || searchText.isNotBlank()

    Column(
        modifier = Modifier
            .width(clamp(min = 1200.px, value = 90.vw, max = 2000.px))
            .padding(leftRight = 3.cssRem),
    ) {
        // Header
        Row(
            modifier = Modifier.fillMaxWidth().padding(top = 80.px, bottom = 32.px),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Column(verticalArrangement = Arrangement.spacedBy(4.px)) {
                SpanText(
                    text = "Manage Wallpapers",
                    modifier = Modifier
                        .fontFamily(FontFamily)
                        .fontSize(32.px)
                        .fontWeight(FontWeight.Bold)
                        .moxTextGradient(),
                )
                SpanText(
                    text = "${state.wallpapers.size} wallpapers loaded",
                    modifier = Modifier
                        .fontFamily(FontFamily)
                        .fontSize(14.px)
                        .color(Colors.Gray),
                )
            }
            GradientButton(
                text = "+ Add Wallpaper",
                onClick = onAddClick,
            )
        }

        // Search bar row — search input + optional Clear Filters chip at its end
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.px),
        ) {
            Box(modifier = Modifier.flexGrow(1)) {
                SearchBarWithAutocomplete(
                    value = searchText,
                    onValueChange = onSearchChange,
                    suggestions = allTags,
                    placeholder = "Search wallpapers…",
                    onSearch = onSearchChange,
                )
            }

            if (hasActiveFilters) {
                Row(
                    modifier = PressableButtonStyle.toModifier()
                        .padding(left = 14.px, top = 8.px, bottom = 6.px, right = 12.px)
                        .borderRadius(16.px)
                        .boxShadow(blurRadius = 2.px, color = Colors.LightGray)
                        .styleModifier { property("background", "#FFFFFF") }
                        .onClick { onClearFilters() },
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SpanText(
                        text = "Clear Filters",
                        modifier = Modifier
                            .fontFamily(FontFamily)
                            .fontSize(clamp(min = MinChipFontSize, value = 0.8.vw, max = MaxChipFontSize))
                            .moxTextGradient(),
                    )
                }
            }
        }

        // Type filter chips
        Row(
            modifier = Modifier.fillMaxWidth().padding(top = 16.px),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(12.px),
        ) {
            ALL_TYPES.forEach { t ->
                Chip(
                    description = t,
                    typeChip = true,
                    isSelected = t in selectedTypes,
                    onChipClick = { onTypeToggle(t) },
                )
            }
        }

        // Series chips — always a single scrollable row with hidden scrollbar.
        if (allSeries.isNotEmpty()) {
            Row(
                modifier = ChipRowScrollStyle.toModifier()
                    .fillMaxWidth()
                    .padding(top = 12.px, bottom = 8.px),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(12.px),
            ) {
                allSeries.forEach { s ->
                    Chip(
                        description = s,
                        isSelected = s in selectedSeries,
                        onChipClick = { onSeriesToggle(s) },
                    )
                }
            }
        }

        // Wallpaper grid
        Box(modifier = Modifier.fillMaxWidth().padding(topBottom = 30.px).flexGrow(1)) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .align(Alignment.TopCenter)
                    .padding(top = 8.px, bottom = 16.px)
                    .flexWrap(FlexWrap.Wrap),
                horizontalArrangement = Arrangement.spacedBy(16.px),
            ) {
                state.wallpapers.forEach { wallpaper ->
                    WallpaperDisplay(
                        wallpaper = wallpaper,
                        hoverText = "Click to Edit",
                        onWallpaperClick = { onWallpaperClick(it) },
                    )
                }
            }
        }

        if (isLoadingMore) {
            Box(
                modifier = Modifier.fillMaxWidth().padding(top = 12.px, bottom = 24.px),
                contentAlignment = Alignment.Center,
            ) { LoadingMoreIndicator() }
        }
    }
}
